import { useMemo } from "react"

import userStore from "userStore"
import { getRolesFromToken } from "helpers/auth_helpers"

// BDSJEA-35: perfiles definidos por el banco en BDSJEA-13.
export const ROL_CONSULTA = "CONSULTA"
export const ROL_ANALISTA = "ANALISTA"
export const ROL_ADMINISTRADOR = "ADMINISTRADOR"

// El JWT trae las authorities sin prefijo, porque el backend desactiva el
// ROLE_ de Spring. El replace queda igual por si algún token viejo lo trae, y
// para no romper si algún día se vuelve a la convención estándar.
const normalizar = rol =>
  (rol || "")
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")

/**
 * Roles del usuario logueado y helpers para decidir qué mostrar.
 *
 * La fuente principal es el JWT, porque es exactamente lo que el backend va a
 * usar para autorizar. userStore queda como respaldo para el instante entre que
 * el token existe y whoami todavía no volvió.
 */
export default function useAuthorization() {
  const rolesStore = userStore(state => state.roles)
  const permisos = userStore(state => state.permisos)

  return useMemo(() => {
    const delToken = getRolesFromToken()
    const origen = delToken.length ? delToken : rolesStore || []
    const roles = origen.map(normalizar)

    const tieneRol = rol => roles.includes(normalizar(rol))
    const tieneAlguno = (...buscados) => buscados.some(tieneRol)

    // Permiso sobre un objeto concreto, resuelto contra la matriz que ya trae
    // userStore. Es lo que hay que usar para mostrar u ocultar acciones: un rol
    // fijo acá contradiría lo que el administrador configuró en pantalla.
    const permisoDe = ruta => {
      const objetivo = (ruta || "").replace(/^\//, "").toLowerCase()
      return (permisos || []).find(
        p => (p.route || "").replace(/^\//, "").toLowerCase() === objetivo,
      )
    }

    return {
      roles,
      tieneRol,
      tieneAlguno,
      puedeConsultarObjeto: ruta => {
        const p = permisoDe(ruta)
        return !!p && (p.consulta || p.modificacion)
      },
      puedeModificarObjeto: ruta => !!permisoDe(ruta)?.modificacion,
      esAdministrador: tieneRol(ROL_ADMINISTRADOR),
      // Un administrador también puede operar, no solo administrar.
      puedeModificar: tieneAlguno(ROL_ANALISTA, ROL_ADMINISTRADOR),
      // Cualquiera de los tres perfiles puede consultar.
      puedeConsultar: tieneAlguno(
        ROL_CONSULTA,
        ROL_ANALISTA,
        ROL_ADMINISTRADOR,
      ),
    }
  }, [rolesStore, permisos])
}
